import { useEffect, useRef } from 'react';
import { callAPI } from '../../services/api.service';
import { CONFIG } from '../../config/mainConfig';
import { Toast } from '../../components/Common/Toast';
import { setBookDataModified } from '../../store/appSlice';
import { setStoryData } from '../../store/storySlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { MidjourneyModeGeneration } from '../../interfaces/book.interface';
import { UserLocalStorageKeysEnum } from '../../interfaces/user.interface';
import { HTTPStatusEnum } from '../../interfaces/log.interface';
import { SecureImg } from '../Common/SecureImage';

interface PageModalProps {
  pageData: any;
  onClose: () => void;
  modalMode: string;
  modalOpen: boolean;
  bookId: string;
  displayBookPages: () => void;
  setShowLowCreditModal: (show: boolean) => void;
  index?: number;
  setLoadingStatus: (pageData: any, status: boolean) => void;
}

const PageModal = ({
                     pageData,
                     onClose,
                     modalMode,
                     modalOpen,
                     bookId,
                     setShowLowCreditModal,
                     setLoadingStatus
                   }: PageModalProps) => {
  const textRef = useRef<HTMLTextAreaElement>(null);
  const sceneRef = useRef<HTMLTextAreaElement>(null);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const storyData = useSelector((state: RootState) => state.story.storyData);
  const dispatch: AppDispatch = useDispatch();

  const callImageGenerator = async (pageKey: string, storyPrompt: string, storyText: string) => {
    const response = await callAPI('api/generate-single-image', { bookId, pageKey, storyPrompt });
    setLoadingStatus(pageData?.pageIndex, false);
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('New Image Will Show Soon');
    } else if (response?.error) {
      Toast(response?.error, true);
      dispatch(setStoryData({ ...storyData, lastHandledErrorAt: Date.now() }));
    } else if (response?.status === HTTPStatusEnum.BAD_REQUEST) {
      dispatch(setStoryData({
        ...storyData,
        creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE
      }));
      Toast(response?.message, true);
      setShowLowCreditModal(true);
    } else {
      Toast('Image Generation Failed! Try Again!', true);
      dispatch(setStoryData({
        ...storyData,
        creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE
      }));
    }
  };

  useEffect(() => {
    if (textRef.current && pageData) {
      const textLength = textRef.current.value.length;
      textRef.current.setSelectionRange(textLength, textLength);
      textRef.current.focus();
    }
    if (sceneRef.current && pageData) {
      const sceneLength = sceneRef.current.value.length;
      sceneRef.current.setSelectionRange(sceneLength, sceneLength);
      sceneRef.current.focus();
    }
  }, [pageData]);

  const handleUpdateBook = (generateNewImage = true) => {
    if (
      (+(storyData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
        +(bookData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE) ||
      bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed
    ) {

      const creditsToDeduct =
        bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed
          ? 0
          : CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;

      dispatch(setStoryData({
        ...storyData,
        creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) - creditsToDeduct
      }));

      const updatedText = textRef.current ? textRef.current.value : '';
      const updatedScene = sceneRef.current ? sceneRef.current.value : '';
      if (pageData.scene !== updatedScene || pageData.text !== updatedText) {
      }

      if (generateNewImage) {
        setLoadingStatus(pageData?.pageIndex, true);
        callImageGenerator(pageData.pageIndex, updatedScene, updatedText);
      }
      if (localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE) === 'yes') {
        dispatch(setBookDataModified(true));
      }
    }
    onClose();
  };

  if (!pageData) return null;
  return (
    <div
      className={`relative z-10 ${modalOpen ? 'block' : 'hidden'}`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity"></div>
      <div className="fixed inset-0 z-10 w-full overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-8 md:p-4 text-center">
          <div
            className="relative transform overflow-hidden rounded-lg bg-storytan px-8 pb-4 pt-5 text-left shadow-xl transition-all  sm:max-w-6xl">
            <h2 className="text-left text-2xl md:text-4xl mb-4 font-bold text-storyblue">
              Edit Page {pageData?.index + 4}
              {modalMode === 'page' ? `- ${pageData?.index + 1}` : ''}
            </h2>
            <div
              className={`grid ${modalMode === 'page' ? 'grid-cols-3 ' : 'grid-cols-1 w-[500px]'} md: gap-6`}
            >
              <div style={{ display: modalMode === 'editText' || modalMode === 'page' ? 'block' : 'none' }}>
                <h3 className="text-sm text-gray-500 font-bold">Page Text </h3>
                <textarea
                  ref={textRef}
                  defaultValue={pageData?.text}
                  className="h-24 md:h-96 text-lg md:text-2xl w-full p-4 border-gray-400 border-2 text-gray-800"
                />
              </div>

              <div className="my-2" style={{ display: modalMode === 'page' ? 'block' : 'none' }}>
                <SecureImg
                  storageSrc={pageData?.imageURLwebp || pageData?.imageURL}
                  alt="Page"
                  className="mx-auto mt-4 w-48 md:w-96 h-48 md:h-96"
                />
              </div>
              <div
                style={{ display: modalMode === 'editPrompt' || modalMode === 'page' ? 'block' : 'none' }}>
                <h3 className="text-sm text-gray-500 font-bold ">
                  This prompt controls the image only and allows you to modify image created
                </h3>
                <textarea
                  autoFocus
                  ref={sceneRef}
                  defaultValue={pageData.scene}
                  className="h-24 md:h-96 text-lg md:text-2xl w-full p-4 border-gray-400 border-2 text-gray-800"
                />
              </div>
            </div>

            <div className="w-full flex justify-end gap-3 mt-1">
              <button
                className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblue px-4 py-2 text-base   text-white shadow-sm"
                onClick={() => handleUpdateBook(false)}
                style={{ display: modalMode === 'editText' || modalMode === 'page' ? 'block' : 'none' }}
              >
                Update Text
              </button>
              <button
                type="button"
                className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblue px-4 py-2 text-base   text-white shadow-sm"
                onClick={() => handleUpdateBook()}
                style={{ display: modalMode === 'editPrompt' || modalMode === 'page' ? 'block' : 'none' }}
              >
                New Image
              </button>
              <button
                className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblue px-4 py-2 text-base   text-white shadow-sm"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PageModal;
